import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import type {
  EventDetails,
  EventOrganizer,
  StudentEvent,
} from "@/services/api/user/events";
import {
  formatRelativeDay,
  localized,
  pickLocalizedName,
  postTypeLabelKey,
  toImageList,
  toUniversityCodes,
  withMomentLocale,
  POST_TYPE_BADGE_CLASS,
} from "@/utils/eventFormat";
import SocietyAvatar from "./SocietyAvatar";
import StudentEventCarousel from "./StudentEventCarousel";
import StudentFollowButton from "./StudentFollowButton";
import StudentSubscribeButton from "./StudentSubscribeButton";
import StudentUniversityLogos from "./StudentUniversityLogos";

interface StudentEventCardProps {
  event: StudentEvent;
  societyIds?: string[];
  onSubscribedChange?: (eventId: string, subscribed: boolean) => void;
  onFollowChange?: (eventId: string, organizerId: string, following: boolean) => void;
}

/**
 * True when one of the student's societies owns the event — the web equivalent
 * of the `feedCreateByUser` check of the RN `EventBox`, which routes a society
 * member to the organizer dashboard instead of the details screen.
 */
const isOwnEvent = (
  details: EventDetails,
  organizers: StudentEvent["organizers"],
  societyIds: string[],
): boolean => {
  if (societyIds.length === 0) return false;
  const owned = new Set(societyIds.map((id) => String(id)));
  const eventSocietyIds = Array.isArray(details.society_id)
    ? details.society_id.map((id) => String(id))
    : [];
  if (eventSocietyIds.some((id) => owned.has(id))) return true;
  return (organizers ?? []).some((organizer) => owned.has(String(organizer.id)));
};

/**
 * Society name shown as the username of a post — the same localized pick as
 * `StudentOrganizerList`: the name up to its first "/", falling back to the
 * Instagram handle and then to the organizer id.
 */
const toDisplayName = (organizer: EventOrganizer, language: string): string => {
  const name = pickLocalizedName(organizer, language) ?? "";
  return name.split("/")[0].trim() || organizer.instagram_username || organizer.id;
};

/**
 * Feed card laid out as an Instagram post, pairing the existing feed functions
 * of the RN `EventBox`: a post header with the society avatar, its bold name,
 * the relative start date and the follow toggle; a full-width cover carousel
 * underneath; then the action row (university marks plus the subscribe
 * bookmark) and the caption — the bold society name and title on one line,
 * followed by the description. No like / comment / share affordances are drawn,
 * and the date, time, location and hashtags live on the details page only. The
 * whole card opens the event; a student who organizes it lands on the edit
 * wizard instead.
 */
const StudentEventCard: React.FC<StudentEventCardProps> = ({
  event,
  societyIds = [],
  onSubscribedChange,
  onFollowChange,
}) => {
  const { t } = useTranslation();
  const language = useSelector(
    (state: any) => state.setting?.language ?? "zh_hant",
  );
  withMomentLocale(language);
  const [subscribed, setSubscribed] = useState(Boolean(event.subscribed));
  const [organizers, setOrganizers] = useState(event.organizers ?? []);

  const { details } = event;
  const coverImages = toImageList(details.event_images);
  const title =
    localized(details.title, details.title_en, language) ||
    t("Untitled Event");
  const description = localized(
    details.description,
    details.description_en,
    language,
  );
  const primary = organizers[0];
  const coOrganizers = organizers.slice(1);
  const displayName = primary ? toDisplayName(primary, language) : "";
  
  const postTypeKey = postTypeLabelKey(details.post_type);
  const linkState = { event: { ...event, organizers, subscribed } };
  const toTarget = isOwnEvent(details, organizers, societyIds)
    ? `/student/events/${details.id}/edit`
    : `/student/events/${details.id}`;

  const handleSubscribedChange = (next: boolean) => {
    setSubscribed(next);
    if (details.id) onSubscribedChange?.(details.id, next);
  };

  const handleFollowChange = (organizerId: string, following: boolean) => {
    setOrganizers((prev) =>
      prev.map((organizer) =>
        organizer.id === organizerId ? { ...organizer, following } : organizer,
      ),
    );
    if (details.id) onFollowChange?.(details.id, organizerId, following);
  };

  return (
    <article className="relative mb-4 overflow-hidden rounded-2xl bg-white shadow-sm transition hover:shadow-md">
      <Link
        to={toTarget}
        state={linkState}
        aria-label={title}
        className="absolute inset-0 rounded-2xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[#ffa217]"
      />

      {primary && (
        <div className="pointer-events-none relative flex items-center gap-2.5 px-3 py-2.5">
          <SocietyAvatar
            icon={primary.society_icon}
            name={displayName}
            className="h-9 w-9 text-sm"
          />
          <span className="min-w-0 flex-1 truncate text-sm text-gray-900">
            <span className="font-bold">{displayName}</span>
            {false && (
              <span className="text-xs text-gray-400"> ""</span>
            )}
          </span>
          <div className="pointer-events-auto shrink-0">
            <StudentFollowButton
              organizerId={primary.id}
              following={Boolean(primary.following)}
              onChange={(following) => handleFollowChange(primary.id, following)}
            />
          </div>
        </div>
      )}

      {coOrganizers.length > 0 && (
        <div className="pointer-events-none relative flex flex-wrap items-center gap-x-4 gap-y-1.5 px-3 pb-2.5">
          {coOrganizers.map((organizer) => (
            <div
              key={organizer.id}
              className="flex min-w-0 items-center gap-1.5"
            >
              <span className="truncate text-xs font-semibold text-gray-500">
                @{toDisplayName(organizer, language)}
              </span>
              <div className="pointer-events-auto shrink-0">
                <StudentFollowButton
                  organizerId={organizer.id}
                  following={Boolean(organizer.following)}
                  onChange={(following) =>
                    handleFollowChange(organizer.id, following)
                  }
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {coverImages.length > 0 && (
        <StudentEventCarousel images={coverImages} alt={title} />
      )}

      <div className="pointer-events-none relative px-3 pb-4 pt-3">
        <div className="mb-2 flex items-center justify-between">
          <StudentUniversityLogos codes={toUniversityCodes(details.university)} />
          <div className="ml-auto flex items-center gap-2">
            {postTypeKey && (
              <span className={`${POST_TYPE_BADGE_CLASS} pointer-events-none`}>
                {t(postTypeKey)}
              </span>
            )}
            <div className="pointer-events-auto">
              <StudentSubscribeButton
                eventId={details.id}
                subscribed={subscribed}
                onChange={handleSubscribedChange}
                variant="icon"
              />
            </div>
          </div>
        </div>

        <h2 className="line-clamp-2 text-sm font-semibold leading-5 text-gray-900">
          {displayName && (
            <span className="text-gray-500">{displayName} </span>
          )}
          <span className="font-bold text-gray-900">{title}</span>
        </h2>

        {description && (
          <p className="mt-1 line-clamp-4 text-sm leading-5 text-gray-500">
            {description}
          </p>
        )}
      </div>
    </article>
  );
};

export default StudentEventCard;
